/** @format */

import { AvsRequest, AvsResponse, Environment } from '@shared/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, retryWhen } from 'rxjs/operators';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { Injectable } from '@angular/core';
import { Logger } from '../logger.service';
import { Observable } from 'rxjs';
import { httpScalingRetryStrategy } from '../../lib/rxjs-operators/http-scaling-retry.operator';

@Injectable({
  providedIn: 'root',
})
export class AvsDao {
  constructor(
    private http: HttpClient,
    private environment: Environment,
    private logger: Logger,
  ) {}

  private DEFAULT_HEADERS = {
    Accept: MediaType.APPLICATION_JSON,
    'Content-Type': MediaType.APPLICATION_JSON,
    'Access-Control-Allow-Origin': '*',
    'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
    Pragma: 'no-cache',
    Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
  };

  // HTTP Headers
  private HTTP_HEADERS = new HttpHeaders(this.DEFAULT_HEADERS);

  /**
   * @returns {Observable<AvsResponse>}
   *
   * @description
   * Function that calls the AvsDao to call Shipping AVS API.  This coarse-grain API can accept an empty Object {}, or
   * an Object with User address {'addressLines': 'STRING[]', 'city': 'STRING', 'state': 'STRING', 'zipcode': 'STRING', 'country': 'STRING' }.
   *
   * @param avsRequest
   */
  public verifyAddress$(avsRequest: AvsRequest = null): Observable<AvsResponse> {
    // HTTP Options
    const httpOptions = {
      headers: this.HTTP_HEADERS,
    };

    httpOptions.headers = httpOptions.headers.set(
      CustomHttpHeaders.X_TIMEOUT,
      `${DCSG_CONSTANTS.timeouts.medium}`,
    );

    // Make the HTTP Request
    return this.http
      .post<AvsResponse>(
        `${this.environment.api.avsApi.origin}${this.environment.api.avsApi.baseUrl}`,
        avsRequest,
        httpOptions,
      )
      .pipe(
        map((avsResponse: AvsResponse) => {
          // Return the raw data
          return avsResponse;
        }),
        retryWhen(
          httpScalingRetryStrategy({
            maxRetryAttempts: 3,
            scalingDuration: 1000,
            excludedStatusCodes: undefined,
            logger: this.logger,
          }),
        ),
      );
  }

  /**
   * @returns {Observable<AvsResponse>}
   *
   * @description
   * Function that calls the Shipping AVS V2 verification endpoint.
   *
   * @param avsRequest
   */
  public verifyAddressV2$(avsRequest: AvsRequest = null): Observable<AvsResponse> {
    const V2_HEADERS = {
      ...this.DEFAULT_HEADERS,
      ProductId: 'CHECKOUT-UI',
      ApplicationId: 'cart',
    };

    const HTTP_V2_HEADERS = new HttpHeaders(V2_HEADERS);

    // HTTP Options
    const httpOptions = {
      headers: HTTP_V2_HEADERS,
    };

    httpOptions.headers = httpOptions.headers.set(
      CustomHttpHeaders.X_TIMEOUT,
      `${DCSG_CONSTANTS.timeouts.medium}`,
    );

    // Make the HTTP Request
    return this.http
      .post<AvsResponse>(
        `${this.environment.api.avsApiV2.origin}${this.environment.api.avsApiV2.baseUrl}`,
        avsRequest,
        httpOptions,
      )
      .pipe(
        map((avsResponse: AvsResponse) => {
          // Return the raw data
          return avsResponse;
        }),
        retryWhen(
          httpScalingRetryStrategy({
            maxRetryAttempts: 3,
            scalingDuration: 1000,
            excludedStatusCodes: undefined,
            logger: this.logger,
          }),
        ),
      );
  }
}
